import React, { useState, useEffect } from 'react';

const NoShowForm = ({ onSave, onCancel, existingData = [] }) => {
  const [rows, setRows] = useState([{ clientId: '', totalAppointments: '', noShows: '' }]);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState(null);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const rowRefs = React.useRef({});
  const pendingScrollRef = React.useRef(false);

  useEffect(() => {
    if (existingData.length > 0) {
      setRows(existingData.map(d => ({
        clientId: d.clientId || '',
        totalAppointments: d.totalAppointments !== undefined ? String(d.totalAppointments) : '',
        noShows: d.noShows !== undefined ? String(d.noShows) : ''
      })));
    }
  }, [existingData]);

  const addRow = () => {
    pendingScrollRef.current = true;
    setRows([...rows, { clientId: '', totalAppointments: '', noShows: '' }]);
  };

  const addMultipleRows = (count) => {
    pendingScrollRef.current = true;
    const newRows = Array.from({ length: count }, () => ({ clientId: '', totalAppointments: '', noShows: '' }));
    setRows([...rows, ...newRows]);
  };

  // Auto-scroll to the newly added row(s) after they render
  useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const el = rowRefs.current[rows.length - 1];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [rows.length]);

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== index));
    setErrors(prev => {
      const updated = {};
      Object.keys(prev).forEach(key => {
        const i = parseInt(key);
        if (i < index) updated[i] = prev[i];
        else if (i > index) updated[i - 1] = prev[i];
      });
      return updated;
    });
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    if (errors[index]) {
      const rowErrors = { ...errors[index] };
      delete rowErrors[field];
      setErrors({ ...errors, [index]: Object.keys(rowErrors).length ? rowErrors : undefined });
    }
  };

  const handleSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;
    const matchIndex = rows.findIndex(row =>
      row.clientId.trim().toLowerCase() === query
    );
    if (matchIndex !== -1) {
      setSearchStatus('found');
      setHighlightedRow(matchIndex);
      rowRefs.current[matchIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setSearchStatus('notfound');
      setTimeout(() => setSearchStatus(null), 2500);
    }
  };

  const validate = () => {
    const newErrors = {};
    rows.forEach((row, i) => {
      const rowErrors = {};
      if (!row.clientId.trim()) rowErrors.clientId = 'Required';
      const total = parseInt(row.totalAppointments, 10);
      if (row.totalAppointments === '' || isNaN(total) || total < 1) {
        rowErrors.totalAppointments = 'Must be ≥1';
      }
      const noShows = parseInt(row.noShows, 10);
      if (row.noShows === '' || isNaN(noShows) || noShows < 0) {
        rowErrors.noShows = 'Must be ≥0';
      }
      if (!isNaN(total) && !isNaN(noShows) && noShows > total) {
        rowErrors.noShows = 'Can\'t exceed total';
      }
      if (Object.keys(rowErrors).length) newErrors[i] = rowErrors;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = rows.map(r => ({
      clientId: r.clientId.trim(),
      totalAppointments: parseInt(r.totalAppointments, 10),
      noShows: parseInt(r.noShows, 10)
    }));
    onSave(data);
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Enter No-Show Data</h2>
      <p className="text-gray-600 mb-1 text-sm">
        Enter client IDs, total scheduled appointments, and number of no-shows.
      </p>
      <p className="text-gray-500 mb-4 text-xs italic">
        One row per client. No-shows cannot exceed total appointments.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={addRow} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold">
          + Add Row
        </button>
        <button onClick={() => addMultipleRows(5)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold">
          + Add 5 Rows
        </button>
        <button onClick={() => addMultipleRows(10)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold">
          + Add 10 Rows
        </button>

        {/* Search Client ID */}
        <div className="flex items-center gap-1 ml-auto">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchStatus(null); setHighlightedRow(null); }}
              onFocus={() => setHighlightedRow(null)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search Client ID..."
              className={`pl-8 pr-3 py-2 text-sm border rounded-lg w-48 focus:outline-none focus:ring-2 transition-colors ${
                searchStatus === 'notfound'
                  ? 'border-red-400 bg-red-50 focus:ring-red-300'
                  : searchStatus === 'found'
                  ? 'border-green-400 bg-green-50 focus:ring-green-300'
                  : 'border-gray-300 focus:ring-blue-300'
              }`}
            />
            {searchStatus === 'notfound' && (
              <span className="absolute -bottom-5 left-0 text-xs text-red-600 whitespace-nowrap">No match found</span>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Go
          </button>
        </div>
      </div>

      {/* Column headers */}
      <div className="flex gap-3 items-center mb-2 text-xs font-semibold text-gray-500 uppercase">
        <span className="w-8 text-right">#</span>
        <span className="flex-1">Client ID</span>
        <span className="w-36">Total Appointments</span>
        <span className="w-28">No-Shows</span>
        <span className="w-6"></span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto mb-6">
        {rows.map((row, i) => (
          <div
            key={i}
            ref={el => rowRefs.current[i] = el}
            className={`flex gap-3 items-start rounded-lg transition-colors duration-300 ${highlightedRow === i ? 'bg-yellow-100 ring-2 ring-yellow-400' : ''}`}
          >
            <span className="text-gray-400 text-sm mt-2 w-8 text-right">{i + 1}.</span>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Client ID"
                value={row.clientId}
                onChange={e => updateRow(i, 'clientId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[i]?.clientId ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors[i]?.clientId && <p className="text-red-500 text-xs mt-1">{errors[i].clientId}</p>}
            </div>
            <div className="w-36">
              <input
                type="number"
                min="1"
                placeholder="Total"
                value={row.totalAppointments}
                onChange={e => updateRow(i, 'totalAppointments', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[i]?.totalAppointments ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors[i]?.totalAppointments && <p className="text-red-500 text-xs mt-1">{errors[i].totalAppointments}</p>}
            </div>
            <div className="w-28">
              <input
                type="number"
                min="0"
                placeholder="No-Shows"
                value={row.noShows}
                onChange={e => updateRow(i, 'noShows', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[i]?.noShows ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors[i]?.noShows && <p className="text-red-500 text-xs mt-1">{errors[i].noShows}</p>}
            </div>
            <button
              onClick={() => removeRow(i)}
              disabled={rows.length <= 1}
              className="mt-1 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <button onClick={handleSave} className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
          Save Data ({rows.length} records)
        </button>
        <button onClick={onCancel} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default NoShowForm;
